import {
  Ban,
  Bed,
  BellRing,
  CalendarClock,
  CheckCheck,
  Circle,
  CircleCheck,
  CircleDot,
  CircleMinus,
  CirclePause,
  CircleX,
  Clock3,
  FileCheck,
  Fuel,
  Gauge,
  Info,
  MapPinOff,
  MapPinPlus,
  Navigation,
  OctagonAlert,
  PackageCheck,
  PackageOpen,
  RouteOff,
  ShieldAlert,
  ShieldCheck,
  SkipForward,
  Split,
  Timer,
  TimerOff,
  TriangleAlert,
  Truck,
  Hourglass,
  AlarmClock,
  Waypoints,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import type {
  AmbitoRegla,
  EstadoAlerta,
  EstadoDiarioUnidad,
  EstadoParada,
  EstadoUnidad,
  EstadoViaje,
  MotivoParadaNoPlanificada,
  NivelAlerta,
  TipoAlerta,
  TipoParada,
} from '@tms/contratos';

/** Color con significado. Siempre se muestra junto a un ícono y un texto. */
export type Color = 'green' | 'amber' | 'red' | 'muted' | 'brand';

export interface Presentacion {
  etiqueta: string;
  color: Color;
  icono: LucideIcon;
}

export const UNIDAD: Record<EstadoUnidad, Presentacion> = {
  EN_RUTA: { etiqueta: 'En ruta', color: 'green', icono: Navigation },
  DETENIDA: { etiqueta: 'Detenida', color: 'amber', icono: CirclePause },
  SIN_REPORTE: { etiqueta: 'Sin reportar', color: 'red', icono: WifiOff },
  SIN_ACTIVIDAD: { etiqueta: 'Sin actividad', color: 'muted', icono: CircleMinus },
};

export const VIAJE: Record<EstadoViaje, Presentacion> = {
  PLANIFICADO: { etiqueta: 'Planificado', color: 'muted', icono: CalendarClock },
  EN_CURSO: { etiqueta: 'En curso', color: 'green', icono: Truck },
  FINALIZADO: { etiqueta: 'Finalizado', color: 'brand', icono: CheckCheck },
  CANCELADO: { etiqueta: 'Cancelado', color: 'red', icono: Ban },
};

export const PARADA: Record<EstadoParada, Presentacion> = {
  PENDIENTE: { etiqueta: 'Pendiente', color: 'muted', icono: Circle },
  CUMPLIDA: { etiqueta: 'Cumplida', color: 'green', icono: CircleCheck },
  EXCEDIDA: { etiqueta: 'Excedida', color: 'amber', icono: TimerOff },
  OMITIDA: { etiqueta: 'Omitida', color: 'red', icono: CircleX },
};

export const ALERTA: Record<EstadoAlerta, Presentacion> = {
  ABIERTA: { etiqueta: 'Abierta', color: 'red', icono: BellRing },
  RESUELTA: { etiqueta: 'Resuelta', color: 'green', icono: CheckCheck },
  JUSTIFICADA: { etiqueta: 'Justificada', color: 'muted', icono: FileCheck },
};

export const DIARIO: Record<EstadoDiarioUnidad, Presentacion> = {
  EN_VIAJE: { etiqueta: 'En viaje', color: 'green', icono: Truck },
  ACTIVA_SIN_VIAJE: { etiqueta: 'Activa sin viaje', color: 'amber', icono: Timer },
  SIN_ACTIVIDAD: { etiqueta: 'Sin actividad', color: 'muted', icono: CircleMinus },
};

/** Nivel de alerta (D-37): rojo crítico, ámbar advertencia, gris informativa. */
export const NIVEL: Record<NivelAlerta, Presentacion> = {
  CRITICA: { etiqueta: 'Crítica', color: 'red', icono: OctagonAlert },
  ADVERTENCIA: { etiqueta: 'Advertencia', color: 'amber', icono: TriangleAlert },
  INFORMATIVA: { etiqueta: 'Informativa', color: 'muted', icono: Info },
};

/** Orden de gravedad: más alto = más grave. */
export const RANGO_NIVEL: Record<NivelAlerta, number> = { INFORMATIVA: 0, ADVERTENCIA: 1, CRITICA: 2 };

/** Ícono por tipo de alerta (el título viene de los contratos). */
export const ICONO_TIPO_ALERTA: Record<TipoAlerta, LucideIcon> = {
  PARADA_NO_AUTORIZADA: CirclePause,
  PARADA_NO_PLANIFICADA: MapPinPlus,
  PARADA_OMITIDA: SkipForward,
  PARADA_EXCEDIDA: TimerOff,
  DESVIO_RUTA: RouteOff,
  CAMBIO_RUTA: Split,
  EXCESO_VELOCIDAD: Gauge,
  RALENTI_EXCESIVO: Hourglass,
  INCONSISTENCIA_MARCACION: MapPinOff,
  SIN_REPORTE: WifiOff,
  INICIO_DEMORADO: AlarmClock,
  RETRASO_ETA: Clock3,
  RECURSO_INHABILITADO: ShieldAlert,
  VIAJE_SIN_RUTA: Waypoints,
};

/** Tipo de parada de una ruta (lo define el despacho). */
export const TIPO_PARADA: Record<TipoParada, { etiqueta: string; icono: LucideIcon }> = {
  CARGA: { etiqueta: 'Carga', icono: PackageOpen },
  DESCARGA: { etiqueta: 'Descarga', icono: PackageCheck },
  COMBUSTIBLE: { etiqueta: 'Combustible', icono: Fuel },
  DESCANSO: { etiqueta: 'Descanso', icono: Bed },
  CONTROL: { etiqueta: 'Control', icono: ShieldCheck },
  OTRO: { etiqueta: 'Otro', icono: CircleDot },
};

/** Motivo de una parada no planificada declarada por el chofer (catálogo provisorio, D-36). */
export const MOTIVO_NO_PLANIFICADA: Record<MotivoParadaNoPlanificada, string> = {
  COMBUSTIBLE: 'Combustible',
  DESCANSO: 'Descanso',
  CONTROL_POLICIAL: 'Control policial',
  MECANICA: 'Mecánica',
  CARGA_DESCARGA: 'Carga o descarga',
  OTRO: 'Otro motivo',
};

/** Ámbito de una regla de alerta, de la más general a la más específica. */
export const AMBITO: Record<AmbitoRegla, string> = {
  GENERAL: 'General',
  EMPRESA: 'Empresa',
  UNIDAD_NEGOCIO: 'Unidad de negocio',
  RUTA: 'Ruta',
  PARADA_RUTA: 'Parada de ruta',
};
